import { CSSProperties, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from "classnames";
import {
    getDCUIHeaderBGColor,
    getDCUIHeaderTextColor,
    getDCUIRPLogoUrl,
    getDCUIRPName,
    getHeaderTheme
} from '@redux/selectors/verification-configuration-selectors';
import { getLanguageContent } from '@redux/selectors/language-selectors';
import { setForceShowAppVersion } from "@redux/actions/common-actions";
import { getForceShowAppVersion } from "@redux/selectors/common-selectors";
import { LogoContainer } from '../logo/logo-container';
import styles from "./header-container.less";

export const HeaderContainer = () => {
    const content = useSelector(getLanguageContent);
    const headerTheme = useSelector(getHeaderTheme);
    const dcuiHeaderBGColor = useSelector(getDCUIHeaderBGColor);
    const dcuiHeaderTextColor = useSelector(getDCUIHeaderTextColor);
    const dcuiRPLogoUrl = useSelector(getDCUIRPLogoUrl);
    const dcuiRPName = useSelector(getDCUIRPName);
    const forceShowAppVersion = useSelector(getForceShowAppVersion);
    const [clickCount, setClickCount] = useState(0);

    const dispatch = useDispatch();

    useEffect(() => {
        if (clickCount > 9 && !forceShowAppVersion) {
            dispatch(setForceShowAppVersion(true));
        } else if (clickCount < 9 && forceShowAppVersion) {
            dispatch(setForceShowAppVersion(false));
        }
    }, [clickCount, forceShowAppVersion]);

    const onClickHeader = () => {
        if (clickCount > 12) {
            setClickCount(0);
        } else {
            setClickCount(clickCount + 1);
        }
    };

    const logoElement = useMemo(() => {
        if (headerTheme.showLogo && headerTheme.logoUrl) {
            return (
                <LogoContainer
                    logoUrl={headerTheme.logoUrl}
                    altText={`${dcuiRPName ? dcuiRPName : ""} Logo`}
                    alignLogo={headerTheme.alignLogo}
                    section={"header"}
                />
            );
        } else if (dcuiRPLogoUrl) {
            return (
                <LogoContainer
                    logoUrl={dcuiRPLogoUrl}
                    altText={`${dcuiRPName ? dcuiRPName : ""} Logo`}
                    section={"header"}
                />
            );
        }
        return null;
    }, [dcuiRPLogoUrl, content]);

    const titleElement = useMemo(() => {
        if (headerTheme.showText && content.header?.text) {
            return (
                <div className={styles.title}>
                    <h3>{content.header.text || dcuiRPName}</h3>
                </div>
            );
        }
        return null;
    }, [dcuiRPName, headerTheme.showText, content.header?.text]);

    const headerTextColor = headerTheme.textColor || dcuiHeaderTextColor;
    const headerBGColor = headerTheme.backgroundColor || dcuiHeaderBGColor;

    const headerStyle: CSSProperties = {
        color: headerTextColor,
        backgroundColor: headerBGColor
    };

    const { showText, showLogo, alignText, alignLogo } = headerTheme;

    const headerContentAutoMinMax = (
        showText &&
        showLogo &&
        alignText === alignLogo
    ) ? styles.autoMinMax : "";

    return (
        <div id="header-container" onClick={onClickHeader} className={styles.headerContainer} style={headerStyle}>
            {(!showText && !showLogo && (
                <div id="header-content" className={styles.headerFill}></div>
            ))}
            {(showText || showLogo) && (
                <div id="header-content" className={classNames(styles.headerContent, headerContentAutoMinMax)}>
                    <div id="header-left-section" className={styles.leftHeaderSection}>
                        {showLogo && alignLogo === "left" && (
                            logoElement
                        )}
                        {showText && alignText === "left" && (
                            titleElement
                        )}
                    </div>
                    <div id="header-center-section" className={styles.centerHeaderSection}>
                        {((showLogo && alignLogo === "center") || dcuiRPLogoUrl) && (
                            logoElement
                        )}
                        {showText && alignText === "center" && (
                            titleElement
                        )}
                    </div>
                    <div id="header-right-section" className={styles.rightHeaderSection}>
                        {showLogo && alignLogo === "right" && (
                            logoElement
                        )}
                        {showText && alignText === "right" && (
                            titleElement
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
